/**
 * Date/time helpers for console tables.
 *
 * The backend serialises timestamps with `datetime.isoformat()` on naive UTC
 * values, so they arrive WITHOUT a zone suffix ("2026-07-17T09:03:28"). Passing
 * that straight to `new Date()` makes the browser read it as LOCAL time, which
 * silently shifts every timestamp by the viewer's UTC offset. Always parse via
 * parseUTCDate.
 */

export const parseUTCDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    if (!dateStr.endsWith("Z") && !/[+-]\d{2}:\d{2}$/.test(dateStr)) {
        return new Date(dateStr + "Z");
    }
    return new Date(dateStr);
};

/** "17 Jul 2026, 2:33 pm" in the viewer's local zone; "—" when absent. */
export const formatDateTime = (value) => {
    const d = parseUTCDate(value);
    if (!d || Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
        day: "2-digit", month: "short", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
    });
};

/** "17 Jul 2026" — date only, in the viewer's local zone; "—" when absent. */
export const formatDate = (value) => {
    const d = parseUTCDate(value);
    if (!d || Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, {
        day: "2-digit", month: "short", year: "numeric",
    });
};

/** Just the local date, for date-range filtering. */
export const toLocalDateOnly = (value) => {
    const d = parseUTCDate(value);
    if (!d || Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
